import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useTranslation } from 'react-i18next';

interface SeoHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  jsonLd?: object | object[];
  /** If true, load title/description/keywords from site_settings DB, overridden by explicit props */
  useDbSeo?: boolean;
}

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://kyrgyzstansurrogate.com';

interface DbSeoData {
  title: string;
  description: string;
  keywords: string;
}

function useSeoFromDb(enabled: boolean): DbSeoData | null {
  const { i18n } = useTranslation();
  const [data, setData] = useState<DbSeoData | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const lang = i18n.language?.slice(0, 2) || 'zh';
    const validLangs = ['zh', 'en', 'ru'];
    const l = validLangs.includes(lang) ? lang : 'zh';

    supabase
      .from('site_settings')
      .select('key, value')
      .in('key', [`seo_title_${l}`, `seo_description_${l}`, `seo_keywords_${l}`])
      .then(({ data: rows }) => {
        if (!rows || rows.length === 0) return;
        const map: Record<string, string> = {};
        rows.forEach((r) => { map[r.key] = r.value || ''; });
        const title = map[`seo_title_${l}`] || '';
        const description = map[`seo_description_${l}`] || '';
        const keywords = map[`seo_keywords_${l}`] || '';
        if (title || description) {
          setData({ title, description, keywords });
        }
      });
  }, [enabled, i18n.language]);

  return data;
}

export default function SeoHead({ title, description, keywords, canonical, jsonLd, useDbSeo = false }: SeoHeadProps) {
  const dbSeo = useSeoFromDb(useDbSeo);

  const finalTitle = title || dbSeo?.title || '';
  const finalDescription = description || dbSeo?.description || '';
  const finalKeywords = keywords || dbSeo?.keywords || '';

  useEffect(() => {
    if (!finalTitle && !finalDescription) return;

    // Title
    if (finalTitle) document.title = finalTitle;

    const setMeta = (selector: string, attr: string, value: string) => {
      if (!value) return;
      let el = document.querySelector<HTMLMetaElement>(selector);
      if (!el) {
        el = document.createElement('meta');
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    if (finalDescription) setMeta('meta[name="description"]', 'content', finalDescription);
    if (finalKeywords) setMeta('meta[name="keywords"]', 'content', finalKeywords);

    // Open Graph
    if (finalTitle) setMeta('meta[property="og:title"]', 'content', finalTitle);
    if (finalDescription) setMeta('meta[property="og:description"]', 'content', finalDescription);

    // Canonical
    if (canonical) {
      let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.appendChild(link);
      }
      link.href = `${SITE_URL}${canonical}`;
    }

    setMeta('meta[name="last-modified"]', 'content', new Date().toISOString().slice(0, 10));
  }, [finalTitle, finalDescription, finalKeywords, canonical]);

  useEffect(() => {
    // JSON-LD
    const existingScripts = document.querySelectorAll('script[data-seo-page-jsonld]');
    existingScripts.forEach((s) => s.remove());

    if (jsonLd) {
      const schemas = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      schemas.forEach((schema) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-seo-page-jsonld', 'true');
        script.textContent = JSON.stringify(schema);
        document.head.appendChild(script);
      });
    }

    return () => {
      document.querySelectorAll('script[data-seo-page-jsonld]').forEach((s) => s.remove());
    };
  }, [jsonLd]);

  return null;
}

export { SITE_URL };